import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Image as ImageIcon, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { PhotosMap } from "@/components/trip/PhotosMap";
import { SEO } from "@/components/SEO";
import { useTrip } from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import { getTranslatedValue } from "@/components/TranslatableField";
import { getUploadUrl, getCloudinaryThumbnail } from "@/lib/utils";
import { createPortal } from "react-dom";

export default function TripGallery() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const { isAuthenticated } = useAuth();
  const { data: trip, isLoading } = useTrip(tripId, { isAuthenticated });

  const [lightbox, setLightbox] = useState(null); // { photos, index }

  // Build photos grouped by segment
  const { segmentGroups, allPhotos, mapPhotos } = useMemo(() => {
    if (!trip) return { segmentGroups: [], allPhotos: [], mapPhotos: [] };

    const groups = [];
    const all = [];

    (trip.segments || []).forEach((seg, segIdx) => {
      const segTitle = getTranslatedValue(seg.title, currentLang) || `${t("trip_form.segment")} ${segIdx + 1}`;
      const photos = (seg.photos || []).map((p) => ({
        ...p,
        segmentLabel: segTitle,
        captionText: typeof p.caption === "object" ? (getTranslatedValue(p.caption, currentLang) || "") : (p.caption || ""),
        isTripMain: p.filename === trip.main_photo,
      }));
      if (photos.length > 0) {
        groups.push({ segmentId: seg.segment_id, title: segTitle, index: segIdx, photos });
        all.push(...photos);
      }
    });

    const mPhotos = all
      .filter((p) => p.gps?.lat && p.gps?.lng)
      .map((p) => ({ ...p, caption_text: p.captionText, segment_label: p.segmentLabel }));

    return { segmentGroups: groups, allPhotos: all, mapPhotos: mPhotos };
  }, [trip, currentLang, t]);

  const openLightbox = (groupPhotos, photoIndex) => {
    setLightbox({ photos: groupPhotos, index: photoIndex });
  };

  const openGlobalLightbox = (photoUrl) => {
    const idx = allPhotos.findIndex((p) => getUploadUrl(p.filename) === photoUrl);
    if (idx >= 0) setLightbox({ photos: allPhotos, index: idx });
  };

  const tripTitle = trip ? (getTranslatedValue(trip.title, currentLang) || "Passeio") : "";

  if (isLoading) {
    return (
      <div className="min-h-screen hero-background">
        <AppHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen hero-background">
        <AppHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-muted-foreground">{t("trip_detail.not_found", "Passeio não encontrado")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-background pt-36" data-testid="trip-gallery-page">
      <SEO
        title={`${tripTitle} — Galeria`}
        description={`Galeria de fotos do passeio "${tripTitle}" na MotoTrips. Vê as imagens partilhadas pela comunidade motard com localização e legendas.`}
        path={`/passeio/${tripId}/galeria`}
      />
      <AppHeader />

      {/* Header bar */}
      <div className="bg-background/70 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={`/passeio/${tripId}`} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-trip">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-foreground truncate">{tripTitle}</h1>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <ImageIcon className="h-3 w-3" />
              {t("trip_gallery.photo_count", "{{count}} fotos").replace("{{count}}", allPhotos.length)}
              {mapPhotos.length > 0 && (
                <span className="text-muted-foreground/60">
                  · {mapPhotos.length} GPS
                </span>
              )}
            </p>
          </div>
        </div>
        {/* Segment shortcuts */}
        {segmentGroups.length > 1 && (
          <div className="max-w-7xl mx-auto px-4 pb-3 flex items-center gap-2 flex-wrap">
            {segmentGroups.map((group) => (
              <a
                key={group.segmentId}
                href={`#etapa-${group.index}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-muted hover:bg-primary/20 hover:text-primary text-muted-foreground transition-colors"
                data-testid={`segment-shortcut-${group.index}`}
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                  {group.index + 1}
                </span>
                {group.title}
                <span className="text-muted-foreground/60">({group.photos.length})</span>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Map section — same max-width as the rest of the page */}
      {mapPhotos.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-6" data-testid="gallery-map-section">
          <PhotosMap
            photos={mapPhotos}
            className="w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden border border-border"
            />
        </div>
      )}

      {/* Gallery by segment */}
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-10">
        {segmentGroups.map((group) => (
          <section key={group.segmentId} id={`etapa-${group.index}`} data-testid={`gallery-segment-${group.index}`} className="scroll-mt-40">
            {/* Segment header */}
            <div className="flex items-center gap-3 mb-5">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
                {group.index + 1}
              </span>
              <div>
                <h2 className="text-base font-semibold text-foreground">{group.title}</h2>
                <p className="text-xs text-muted-foreground">{group.photos.length} {group.photos.length === 1 ? "foto" : "fotos"}</p>
              </div>
              <div className="h-px flex-1 bg-border ml-2" />
            </div>

            {/* Photo grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {group.photos.map((photo, idx) => {
                const photoUrl = getUploadUrl(photo.filename);
                const thumbUrl = getCloudinaryThumbnail(photoUrl, 400, 300);
                return (
                  <div
                    key={photo.filename}
                    className="group cursor-pointer"
                    onClick={() => openLightbox(group.photos, idx)}
                    data-testid={`gallery-photo-${group.index}-${idx}`}
                  >
                    <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                      <img
                        src={thumbUrl}
                        alt={photo.captionText || `Foto ${idx + 1}`}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {photo.isTripMain && (
                        <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <ImageIcon className="h-3 w-3" />
                          {t("trip_gallery.cover", "Capa")}
                        </div>
                      )}
                    </div>
                    {photo.captionText && (
                      <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2" title={photo.captionText}>
                        {photo.captionText}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Back to top */}
            <div className="flex justify-end mt-4">
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors" data-testid={`segment-top-link-${group.index}`}>
                <ArrowLeft className="h-3 w-3 rotate-90" />
                Topo
              </button>
            </div>
          </section>
        ))}

        {allPhotos.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>{t("trip_gallery.no_photos", "Este passeio ainda não tem fotos.")}</p>
          </div>
        )}
      </div>

      <Footer />

      {/* Lightbox */}
      {lightbox && createPortal(
        <GalleryLightbox
          photos={lightbox.photos}
          initialIndex={lightbox.index}
          currentLang={currentLang}
          onClose={() => setLightbox(null)}
        />,
        document.body
      )}
    </div>
  );
}

function GalleryLightbox({ photos, initialIndex, currentLang, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const photo = photos[index];
  if (!photo) return null;

  const photoUrl = getUploadUrl(photo.filename);
  const caption = photo.captionText || (typeof photo.caption === "object" ? (getTranslatedValue(photo.caption, currentLang) || "") : (photo.caption || ""));
  const segLabel = photo.segmentLabel || null;

  const prev = () => setIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
  const next = () => setIndex((i) => (i < photos.length - 1 ? i + 1 : 0));

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") prev();
    else if (e.key === "ArrowRight") next();
    else if (e.key === "Escape") onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 flex flex-col items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      ref={(el) => el?.focus()}
      data-testid="gallery-lightbox"
    >
      {/* Close */}
      <button className="absolute top-4 right-4 text-white/70 hover:text-white z-10" onClick={onClose}>
        <X className="h-8 w-8" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/60 text-sm z-10">
        {index + 1} / {photos.length}
      </div>

      {/* Nav prev */}
      {photos.length > 1 && (
        <button
          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white/80 hover:text-white z-10 transition-colors"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Image */}
      <img
        src={photoUrl}
        alt={caption || `Foto ${index + 1}`}
        className="max-w-[90vw] max-h-[78vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Nav next */}
      {photos.length > 1 && (
        <button
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white/80 hover:text-white z-10 transition-colors"
          onClick={(e) => { e.stopPropagation(); next(); }}
          data-testid="lightbox-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Caption + Segment */}
      {(caption || segLabel) && (
        <div className="mt-4 text-center" onClick={(e) => e.stopPropagation()}>
          {segLabel && (
            <span className="text-sm text-white/60 bg-white/10 px-3 py-1 rounded-full">{segLabel}</span>
          )}
          {caption && (
            <p className="text-base text-white/90 mt-2 max-w-xl">{caption}</p>
          )}
        </div>
      )}
    </div>
  );
}
